import { create } from "zustand";
import { doc, getDoc } from 'firebase/firestore';

interface ChatMessage {
  id: string;
  sender: 'user' | 'bot';
  message: string;
  timestamp: number;
  hidden?: boolean; // For internal flags not shown to user
}

interface ChatbotConfig {
  apiKey: string;
  enabled: boolean;
}

interface ChatbotState {
  isOpen: boolean;
  messages: ChatMessage[];
  config: ChatbotConfig | null;
  loading: boolean;
  error: string | null;
  
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  addFilterResetFlag: () => void;
  getVisibleMessages: () => ChatMessage[];
  getFilteredChatHistory: () => { sender: string; message: string }[];
  fetchConfig: (firebase: any) => Promise<void>;
  clearChat: () => void;
}

export const useChatbotStore = create<ChatbotState>((set, get) => ({
  isOpen: false,
  messages: [],
  config: null,
  loading: false,
  error: null,
  
  toggleChat: () => set(state => ({ isOpen: !state.isOpen })),
  openChat: () => set({ isOpen: true }),
  closeChat: () => set({ isOpen: false }),
  
  addMessage: (message) => set(state => ({ 
    messages: [
      ...state.messages, 
      { 
        ...message, 
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, 
        timestamp: Date.now() 
      }
    ] 
  })),

  addFilterResetFlag: () => {
    //console.log('[FILTER RESET] Adding filter reset flag to chat history');
    set(state => ({ 
      messages: [
        ...state.messages, 
        { 
          id: `reset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          sender: 'bot',
          message: '[FILTER_RESET]',
          timestamp: Date.now(),
          hidden: true
        }
      ] 
    }));
  },

  getVisibleMessages: () => {
    const { messages } = get();
    return messages.filter(msg => !msg.hidden);
  },

  getFilteredChatHistory: () => {
    const { messages } = get();
    
    // Return all messages including hidden reset flags, but in the format expected by the prompt
    // The prompt will handle ignoring messages before reset flags
    return messages.map(msg => ({
      sender: msg.sender,
      message: msg.message,
      hidden: msg.hidden || false
    }));
  },
  
  fetchConfig: async (firebase: any) => {
    set({ loading: true, error: null });
    try {
      if (!firebase) return;
      const configDoc = doc(firebase.db, 'chatbotConfig', 'gemini');
      const docSnap = await getDoc(configDoc);
      if (docSnap.exists()) {
        set({ config: docSnap.data() as ChatbotConfig });
      } else {
        set({ error: 'Chatbot configuration not found' });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'An unknown error occurred' });
    } finally {
      set({ loading: false });
    }
  },
  
  clearChat: () => set({ messages: [] })
}));